<script lang="ts">
import {
  computed,
  defineComponent,
  inject,
  nextTick,
  onMounted,
  ref,
  toRef,
  watch,
} from 'vue'
import { useField } from 'vee-validate'

let id = 0

export default defineComponent({
  name: 'base-textarea',
  inheritAttrs: false,
  props: {
    modelValue: {
      type: [String, Number],
      default: '',
    },
    raw: {
      type: [String, Number],
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    name: {
      type: String,
      default: '',
    },
    rules: {
      type: [String, Object],
      default: '',
    },
    error: {
      type: String,
      default: '',
    },
    maxlength: {
      type: Number,
      default: undefined,
    },
    required: {
      type: Boolean,
      default: false,
    },
    classes: {
      type: Object,
      default: () => ({}),
    },
    useRaw: {
      type: Boolean,
      default: true,
    },
    placeholder: {
      type: String,
      default: '',
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    showCounter: {
      type: Boolean,
      default: true,
    },
    autocomplete: {
      type: String,
      default: 'off',
    },
    staticErrorSlot: {
      type: Boolean,
      default: false,
    },
    isFloatLabel: {
      type: Boolean,
      default: false,
    },
    look: {
      type: String,
      default: '',
      validator: (v: string) => !v || /clear|dense/.test(v),
    },
    rows: {
      type: [Number, String],
      default: 3,
    },
  },
  emits: [
    'update:modelValue',
    'update:raw',
  ],
  setup(props, ctx) {
    const rawValue = ref<string | number>('')
    const inputValue = ref<string | number>('')
    const element = ref({} as HTMLElement)
    const name = toRef(props, 'name')
    const label = toRef(props, 'label')

    const isPlaceholderVisible = ref(false)

    const inputId = (() => {
      id += 1
      return `textarea-${id}`
    })()

    const rules = computed(() => {
      if (props.disabled) return ''
      if (props.rules) return props.rules
      return props.required ? 'required' : ''
    })

    const form: any = inject('form', null)

    const vee = props.name && form
      ? useField(name.value, rules.value, {
        validateOnMount: false,
        validateOnValueUpdate: false,
        label: label.value,
      })
      : null

    const isFocused = ref(false)
    const isDisabled = computed(() => form?.disabled || props.disabled)

    const isLookClear = computed(() => {
      return props.look === 'clear'
    })

    const isLabelExpanded = computed(() => {
      return isFocused.value || Boolean(inputValue.value)
    })

    const errorProxy = ref('')

    const errorMessage = computed(() => {
      return isDisabled.value ? '' : (props.error || vee?.errorMessage.value)
    })

    const placeholderProxy = computed(() => {
      if (props.isFloatLabel) {
        if (!isPlaceholderVisible.value || (props.label && !isLabelExpanded.value)) {
          return ''
        }
      }

      return props.placeholder
    })

    const isRequired = computed(() => {
      if (isDisabled.value) return false
      if (rules.value) {
        if (typeof rules.value === 'string') {
          return /required/.test(rules.value)
        } else if (typeof rules.value === 'object') {
          return !!rules.value?.required
        }
      }

      return props.required
    })

    function setValue(_value: any) {
      let value

      if (typeof _value === 'number') {
        value = isNaN(_value) ? '' : _value
      } else {
        value = _value ?? ''
      }

      inputValue.value = value
      rawValue.value = value

      vee?.handleChange(props.useRaw ? rawValue.value : inputValue.value, false)

      if (errorMessage.value) {
        vee?.validate()
      }

      ctx.emit('update:modelValue', inputValue.value)
      ctx.emit('update:raw', rawValue.value)
    }

    function setInitialValue() {
      nextTick(() => {
        const value = props.raw || props.modelValue || vee?.value.value

        if (value) {
          setValue(value)
        }
      })
    }

    onMounted(async () => {
      setInitialValue()
    })

    watch(() => props.modelValue, modelPropValue => {
      if (modelPropValue !== inputValue.value) {
        setValue(modelPropValue)
      }
    })

    {
      let timer: any = 0

      watch(isFocused, (v) => {
        if (timer) clearTimeout(timer)

        if (v) {
          timer = setTimeout(() => {
            isPlaceholderVisible.value = true
          }, 150)
        } else {
          isPlaceholderVisible.value = false
        }
      })
    }

    if (form && vee) {
      watch(() => vee.value.value, newValue => {
        if (props.useRaw && newValue !== rawValue.value) {
          return setValue(newValue)
        }

        if (!props.useRaw && newValue !== inputValue.value) {
          setValue(newValue)
        }
      })
    }

    watch(() => props.raw, rawPropValue => {
      if (rawPropValue !== rawValue.value) {
        setValue(rawPropValue)
      }
    })

    return {
      inputId,
      inputValue,
      element,
      errorMessage,
      errorProxy,
      placeholderProxy,
      isDisabled,
      isRequired,
      isLookClear,
      isLabelExpanded,
      handlers: {
        input: (e: any) => {
          const value = e.target?.value

          e.stopPropagation()
          e.stopImmediatePropagation()
          e.preventDefault()

          setValue(value)
        },
        change: () => {
          vee?.handleChange(rawValue.value)
        },
        focus: () => {
          isFocused.value = true
        },
        blur: (e: any) => {
          isFocused.value = false

          setValue(String(e.target.value ?? '').trim())

          if (name.value && props.rules && inputValue.value) {
            vee?.handleBlur()
            vee?.validate()
          }
        },
      },
    }
  },
})
</script>

<template>
  <div
    ref="root"
    :data-input-name="name"
    class="relative"
  >
    <label
      v-if="label"
      :for="inputId"
      class="block max-w-full truncate border-0 text-label mb-label"
    >
      {{ label }} {{  required ? '*' : '' }}
    </label>
    <div
      :class="[
        isDisabled ? '' : '',
        !readonly && 'focus-within:ring-1 focus-within:ring-input-focused focus-within:border-input-focused',
        isLookClear && 'border-0',
      ]"
      class="flex z-10 items-start text-lg rounded border bg-input border-input"
    >
      <div class="relative flex-1 shrink-0">
        <textarea
          :id="inputId"
          ref="element"
          :autocomplete="autocomplete"
          :disabled="isDisabled"
          :maxlength="maxlength"
          :name="name"
          :placeholder="placeholderProxy"
          :readonly="readonly"
          :rows="rows"
          :value="inputValue"
          class="px-3 py-2 w-full font-sans bg-transparent disabled:bg-transparent focus:outline-none disabled:opacity-50 appearance-none disabled:cursor-not-allowed text-input text-default interactive placeholder:text-placeholder focus:border-input-focused rounded"
          v-bind="$attrs"
          v-on="handlers"
        />
      </div>
    </div>
    <div
      v-if="staticErrorSlot || (showCounter && maxlength) || errorProxy || errorMessage || $slots.default"
      class="flex pt-px text-sm leading-5"
    >
      <div class="mt-1 w-full leading-tight">
        <slot
          :error="errorMessage"
          name="error"
        >
          <div
            :class="[
              staticErrorSlot && 'min-h-5'
            ]"
            class="text-danger"
            data-error
          >
            {{ errorMessage || errorProxy }}
          </div>
        </slot>
      </div>

      <div
        v-if="maxlength && showCounter"
        class="shrink-0 ml-6 leading-5 text-right min-w-24 text-light"
      >
        <span>{{ String(inputValue).length }}</span>
        <span class="mx-px">
          /
        </span>
        <span>{{ maxlength }}</span>
      </div>
    </div>
  </div>
</template>
